import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import * as Sentry from '@sentry/tanstackstart-react'
import { usePostHog } from 'posthog-js/react'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { Toaster } from 'sonner'
import { useEffect } from 'react'
import appCss from '../styles.css?url'
import type { QueryClient } from '@tanstack/react-query'
import { NavBar } from '@/components/NavBar'
import { useWebsocket } from '@/hooks/useWebsocket'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { useBalanceStore } from '@/domains/balance/store'
import { useUserQuery, userQueryOptions } from '@/domains/user/queries'
import { authQueryOptions } from '@/domains/auth/queries'
import { getRefreshToken } from '@/domains/auth/lib'
import { TooltipProvider } from '@/components/ui/tooltip'

interface MyRouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: "Powka – NL Texas Hold'em",
      },
      {
        name: 'description',
        content:
          "Play no-limit Texas Hold'em poker online with USDC on Base. Real money poker with only 1% rake and instant payouts. Join Powka and start playing today.",
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: 'https://powka.app/',
      },
      {
        property: 'og:title',
        content: "Powka – Play No-Limit Texas Hold'em Poker Online",
      },
      {
        property: 'og:description',
        content:
          "Play no-limit Texas Hold'em poker online with USDC on Base. Real money poker with only 1% rake and instant payouts.",
      },
      {
        name: 'twitter:card',
        content: 'summary',
      },
      {
        name: 'twitter:title',
        content: "Powka – Play No-Limit Texas Hold'em Poker Online",
      },
      {
        name: 'twitter:description',
        content:
          "Play no-limit Texas Hold'em poker online with USDC on Base. Real money poker with only 1% rake and instant payouts.",
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/favicon.svg',
      },
      {
        rel: 'manifest',
        href: '/manifest.json',
      },
      {
        rel: 'canonical',
        href: 'https://powka.app/',
      },
    ],
  }),
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Page not found</p>
    </div>
  ),
  beforeLoad: async ({ context }) => {
    const { queryClient } = context

    const refreshToken = await getRefreshToken()
    if (!refreshToken) return

    try {
      const { access_token } = await queryClient.ensureQueryData(
        authQueryOptions(refreshToken),
      )
      await queryClient.ensureQueryData(userQueryOptions(access_token))
    } catch {
      // Token is invalid or expired — continue as unauthenticated
    }
  },
  component: RootLayout,
  shellComponent: RootDocument,
})

function RootLayout() {
  const { data: user } = useUserQuery()
  const posthog = usePostHog()

  // Instantiate the websocket connection
  useWebsocket()

  // Proactively refresh the access token before it expires
  useTokenRefresh(!!user)

  // Hydrate any stores that will receive further updates from the websocket
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null)
      posthog.reset()
      return
    }
    useBalanceStore.setState({ balance: user.balance })
    Sentry.setUser({ id: user.user.id, username: user.user.username })
    posthog.identify(user.user.id, {
      username: user.user.username,
      email: user.user.email,
    })
  }, [user, posthog])

  return (
    <div className="min-h-dvh w-full flex flex-col lg:max-w-[1800px] lg:mx-auto">
      <NavBar />
      <div className="flex-1 w-full flex flex-col">
        <Outlet />
      </div>
    </div>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-17998383948"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-17998383948');
            `,
          }}
        />
      </head>
      <body>
        <TooltipProvider>
          <Sentry.ErrorBoundary
            fallback={({ error, resetError }) => (
              <div className="flex flex-col items-center justify-center min-h-dvh gap-4">
                <h1 className="text-2xl font-bold">Something went wrong</h1>
                <p className="text-muted-foreground">
                  {error instanceof Error
                    ? error.message
                    : 'An unexpected error occurred'}
                </p>
                <button
                  onClick={resetError}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded"
                >
                  Try again
                </button>
              </div>
            )}
          >
            {children}
          </Sentry.ErrorBoundary>
          <Toaster />
        </TooltipProvider>
        {import.meta.env.DEV && (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
          />
        )}
        <Scripts />
      </body>
    </html>
  )
}
